import React from 'react'
import {
  PiCalendarBlank,
  PiGauge,
  PiGridNine,
  PiTreePalm,
  PiUser,
  PiUsersThree,
  PiCirclesThreePlus,
  PiBell,
  PiMapPin,
  PiFiles,
  PiFileText,
  PiConfettiBold,
  PiGear,
  PiSliders,
  PiBuildings,
  PiPuzzlePiece,
  PiMicrosoftExcelLogo,
  PiCurrencyDollar,
  PiChartLine,
  PiChartBar,
  PiLightning,
  PiPlugs,
} from 'react-icons/pi'
import { allPlatforms } from '../../constants'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FrontendUrls } from '../../types/urls'

export interface CanShowRequestToilParams {
  role: string
  plan?: string
  hasToilFeatureFlag: boolean
  companyToilEnabled?: boolean
  ownLocationLeavePolicies?: { toil?: boolean; toilRequestsAllowedForUsers?: boolean }[]
}

// Decides whether the "Request TOIL" option is shown in the Request Leave menu.
// Users need a TOIL-enabled policy they can self-request from in their own location.
// Everyone else (admins, location admins, approvers) only sees it when the company
// actually has TOIL enabled somewhere
export const canShowRequestToil = (params: CanShowRequestToilParams): boolean => {
  const { role, plan, hasToilFeatureFlag, companyToilEnabled, ownLocationLeavePolicies } = params

  if (plan !== SubscriptionPlanEnum.complete && !hasToilFeatureFlag) {
    return false
  }

  if (role === 'User') {
    return Boolean(ownLocationLeavePolicies?.some((lp) => lp.toil && lp.toilRequestsAllowedForUsers))
  }

  return Boolean(companyToilEnabled)
}

// Request Leave dropdown options (will be used in SidebarContent as a separate button)
export const requestLeaveOptions = [
  {
    path: '/app/leaves/request-leave',
    isLink: false,
    title: 'app.requestLeave',
    roles: ['Admin', 'LocationAdmin', 'User', 'Approver'],
    platform: allPlatforms,
  },
  {
    path: '/app/leaves/add-leave',
    isLink: false,
    title: 'app.addLeave',
    roles: ['Admin', 'LocationAdmin', 'Approver'],
    platform: allPlatforms,
  },
  {
    path: '/app/request-toil',
    isLink: false,
    title: 'app.requestToil',
    roles: ['Admin', 'LocationAdmin', 'User', 'Approver'],
    plan: SubscriptionPlanEnum.complete,
    platform: allPlatforms,
    featureFlagName: FeatureFlagEnum.toil,
    ignorePlan: true,
  },
]

const menus = [
  // Direct Dashboard items (no section header)
  // {
  //   path: '/app/dashboard',
  //   isLink: false,
  //   icon: <PiGauge />,
  //   title: 'sidebar.dashboard',
  //   roles: ['Admin', 'Approver'],
  //   platform: allPlatforms,
  //   hideIfFeatureFlagName: FeatureFlagEnum.roleAdaptiveDashboard,
  //   joyRideClass: 'joyride-dashboard-menu',
  // },
  {
    path: '/app/home',
    isLink: false,
    icon: <PiGauge />,
    title: 'app.home',
    roles: ['Admin', 'LocationAdmin', 'Approver'],
    platform: allPlatforms,
    joyRideClass: 'joyride-dashboard-menu',
  },
  {
    path: '/app/my-dashboard',
    isLink: false,
    icon: <PiGauge />,
    title: 'sidebar.dashboard',
    roles: ['User'],
    platform: allPlatforms,
    joyRideClass: 'joyride-dashboard-menu',
  },
  {
    path: '/app/calendar',
    isLink: false,
    icon: <PiCalendarBlank />,
    title: 'app.calendar',
    roles: ['Admin', 'LocationAdmin', 'User', 'Approver'],
    platform: allPlatforms,
    joyRideClass: 'joyride-calendar-menu',
  },
  {
    path: '/app/wallchart',
    isLink: false,
    icon: <PiGridNine />,
    title: 'app.wallchart',
    roles: ['Admin', 'LocationAdmin', 'Approver', 'User'],
    accessSetting: 'wallchartAccess',
    platform: allPlatforms,
    joyRideClass: 'joyride-calendar-menu',
  },

  // TEAM & APPROVAL FLOWS SECTION
  {
    path: '__section__teamApprovalFlows',
    isSection: true,
    icon: <PiUsersThree />,
    title: 'sidebar.section.teamApprovalFlows',
    roles: ['Admin', 'LocationAdmin', 'Approver'],
    platform: allPlatforms,
  },
  {
    path: '/app/users',
    isLink: false,
    icon: <PiUser />,
    title: 'app.users',
    roles: ['Admin', 'LocationAdmin', 'Approver'],
    platform: allPlatforms,
    joyRideClass: 'joyride-users-menu',
  },
  {
    path: FrontendUrls.departments,
    isLink: false,
    icon: <PiCirclesThreePlus />,
    title: 'app.departments',
    roles: ['Admin', 'LocationAdmin'],
    platform: allPlatforms,
    joyRideClass: 'joyride-teams-menu',
  },
  {
    path: '/app/notifications',
    isLink: false,
    icon: <PiBell />,
    title: 'sidebar.timeOffUpdates',
    roles: ['Admin', 'LocationAdmin', 'Approver'],
    platform: allPlatforms,
    joyRideClass: 'joyride-notifications',
  },
  {
    path: FrontendUrls.teamEnergy,
    isLink: false,
    icon: <PiLightning />,
    title: 'sidebar.teamEnergy',
    roles: ['Admin', 'Approver'],
    accessSetting: 'teamEnergyAccess',
    excludeLocationAdmin: true,
    platform: allPlatforms,
    posthogFeatureFlagName: FeatureFlagEnum.teamEnergy,
  },

  // LEAVE SETUP SECTION
  {
    path: '__section__leaveSetup',
    isSection: true,
    isCollapsible: true,
    icon: <PiTreePalm />,
    title: 'sidebar.section.leaveSetup',
    roles: ['Admin', 'LocationAdmin'],
    platform: allPlatforms,
    children: [
      {
        path: FrontendUrls.locations,
        isLink: false,
        icon: <PiMapPin />,
        title: 'app.locations',
        roles: ['Admin', 'LocationAdmin'],
        platform: allPlatforms,
        joyRideClass: 'joyride-location-menu',
      },
      {
        path: FrontendUrls.leaveTypes,
        isLink: false,
        icon: <PiFiles />,
        title: 'app.leaveTypes',
        roles: ['Admin'],
        platform: allPlatforms,
        joyRideClass: 'joyride-leave-types-menu',
      },
      {
        path: FrontendUrls.leavePolicies,
        isLink: false,
        icon: <PiFileText />,
        title: 'sidebar.leavePolicies',
        roles: ['Admin', 'LocationAdmin'],
        platform: allPlatforms,
        joyRideClass: 'joyride-leave-policies-menu',
      },
    ],
  },

  // User-specific items for non-admins
  {
    path: '/app/holidays',
    isLink: false,
    icon: <PiConfettiBold />,
    title: 'app.holidays',
    roles: ['User', 'Approver'],
    platform: allPlatforms,
    joyRideClass: 'joyride-holidays-menu-user',
  },
  {
    path: '/app/my-profile',
    isLink: false,
    icon: <PiUser />,
    title: 'app.myProfile',
    joyRideClass: 'joyride-my-profile-menu',
    roles: ['User', 'Approver'],
    platform: allPlatforms,
  },
  {
    path: FrontendUrls.integrations,
    isLink: false,
    icon: <PiPlugs />,
    title: 'app.integrations',
    roles: ['User'],
    platform: allPlatforms,
  },
  // APPROVERS ONLY SECTION
  // Addons for approvers, because they can't see full workspace settings
  {
    path: '__section__workspaceSettings',
    isSection: true,
    icon: <PiBuildings />,
    title: 'sidebar.section.workspaceSettings',
    roles: ['Approver', 'LocationAdmin'],
    platform: allPlatforms,
  },
  {
    path: FrontendUrls.addons,
    isLink: false,
    icon: <PiPuzzlePiece />,
    title: 'app.addons',
    roles: ['Approver'],
    allowLocationAdminApprover: true,
    tooltip: {
      tagTitle: '1 New',
      hideAfter: new Date('2024-08-31T00:00:00.000'),
    },
    platform: allPlatforms,
  },
  {
    path: FrontendUrls.integrations,
    isLink: false,
    icon: <PiPlugs />,
    title: 'app.integrations',
    roles: ['Approver', 'LocationAdmin'],
    allowLocationAdminApprover: true,
    platform: allPlatforms,
  },
  // END APPROVERS ONLY SECTION

  // ADMIN ONLY SECTION
  // WORKSPACE SETTINGS SECTION
  {
    path: '__section__workspaceSettings',
    isSection: true,
    isCollapsible: true,
    icon: <PiBuildings />,
    title: 'sidebar.section.workspaceSettings',
    roles: ['Admin'],
    platform: allPlatforms,
    children: [
      {
        path: '/app/settings/general',
        isLink: false,
        icon: <PiGear />,
        title: 'app.general',
        roles: ['Admin'],
        platform: allPlatforms,
      },
      {
        path: FrontendUrls.featuresPermissions,
        isLink: false,
        icon: <PiSliders />,
        title: 'sidebar.featuresPermissions',
        roles: ['Admin'],
        platform: allPlatforms,
      },
      // Addons only for admins, because approvers cant see workspace settings
      {
        path: FrontendUrls.addons,
        isLink: false,
        icon: <PiPuzzlePiece />,
        title: 'app.addons',
        roles: ['Admin'],
        tooltip: {
          tagTitle: '1 New',
          hideAfter: new Date('2024-08-31T00:00:00.000'),
        },
        platform: allPlatforms,
      },
      {
        path: FrontendUrls.integrations,
        isLink: false,
        icon: <PiPlugs />,
        title: 'app.integrations',
        roles: ['Admin'],
        platform: allPlatforms,
      },
      {
        path: '/app/imports',
        isLink: false,
        icon: <PiMicrosoftExcelLogo />,
        title: 'sidebar.bulkUploads',
        tooltip: {
          tagTitle: 'New',
          hideAfter: new Date('2025-02-02T00:00:00.000'),
        },
        roles: ['Admin'],
        joyRideClass: 'joyride-notifications',
        platform: allPlatforms,
      },
      {
        path: '/app/settings/billing',
        isLink: false,
        icon: <PiCurrencyDollar />,
        title: 'sidebar.billing',
        roles: ['Admin'],
        platform: allPlatforms,
      },
    ],
  },

  // REPORTS SECTION
  {
    path: '__section__reports',
    isSection: true,
    isCollapsible: true,
    icon: <PiChartLine />,
    title: 'sidebar.section.reports',
    roles: ['Admin', 'Approver'],
    accessSetting: 'reportsAccess',
    platform: allPlatforms,
    children: [
      {
        path: FrontendUrls.reports,
        isLink: false,
        icon: <PiChartBar />,
        title: 'sidebar.reports',
        roles: ['Admin', 'Approver'],
        accessSetting: 'reportsAccess',
        tooltip: {
          tagTitle: 'New',
          hideAfter: new Date('2025-02-02T00:00:00.000'),
        },
        platform: allPlatforms,
      },
    ],
  },
  // END ADMIN ONLY SECTION

  // RECHARGE TYPE - Rendered as custom card in SidebarContent, not as menu item
]

export default menus
